import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Filter } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { projects } from '../data/mock';

const PROJECTS_ORDER = [
  "denial-review-workbench",
  "vifei-suite",
  "agent-cassette",
  "flow-profile",
  "aliveca",
  "mission-relay",
  "ithi-vscode",
  "helios",
  "kura-kura",
  "warp-records-mv",
  "zodia",
  "labubu",
  "resofleur",
  "nlp-pipeline",
  "interactive-lights",
  "dad-a-base",
  "notorious-big-o",
];
const FULL_STACK = "Full-Stack";

const LINK_LABELS = {
  "ithi-vscode": "View Extension",
  "nlp-pipeline": "Contracted By",
  "interactive-lights": "Read Coverage",
};

const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const orderedProjects = (() => {
    const byId = new Map(projects.map(p => [p.id, p]));
    const front = PROJECTS_ORDER.map(id => byId.get(id)).filter(Boolean);
    const seen = new Set(PROJECTS_ORDER);
    const tail = projects.filter(p => !seen.has(p.id));
    return [...front, ...tail];
  })();

  const dataCategories = [...new Set(orderedProjects.map(p => p.category))];
  const categories = ['All', FULL_STACK, ...dataCategories];

  const filteredProjects = selectedCategory === 'All'
    ? orderedProjects
    : selectedCategory === FULL_STACK
      ? orderedProjects.filter(p => (p.tags || []).includes(FULL_STACK))
      : orderedProjects.filter(p => p.category === selectedCategory);

  return (
    <main id="main-content" className="pt-24 sm:pt-32 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <section className="max-w-4xl mx-auto mb-12 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
            Projects
          </h1>
          <p className="text-lg text-muted-foreground">
            A showcase of full-stack applications, AI tools, and creative technology projects
          </p>
        </section>

        {/* Filter */}
        <section className="max-w-6xl mx-auto mb-12">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filter by category:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className="transition-all"
              >
                {category}
              </Button>
            ))}
          </div>
        </section>

        {/* Projects Grid */}
        <section className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <Card key={project.id} className="flex flex-col hover:shadow-lg transition-all group">
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant="secondary" className="text-xs">{project.category}</Badge>
                    {project.featured && (
                      <Badge variant="outline" className="text-xs border-primary text-primary">
                        Featured
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-xl group-hover:text-primary transition-colors">
                    {project.title}
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">{project.year}</p>
                </CardHeader>
                <CardContent className="flex-1">
                  <p className="text-sm text-muted-foreground mb-4">
                    {project.description}
                  </p>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-semibold mb-1">Outcome:</p>
                      <p className="text-xs text-muted-foreground">{project.outcome}</p>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {project.tags.map((tag, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex gap-2">
                  {project.link && project.link.startsWith('http') ? (
                    <>
                      <Button asChild variant="outline" className="flex-1 group/btn">
                        <a href={project.link} target="_blank" rel="noopener noreferrer">
                          {LINK_LABELS[project.id] || (project.link.includes('marketplace.visualstudio.com') ? 'View Extension' : 'View Project')}
                          <ExternalLink className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                        </a>
                      </Button>
                      {project.github && (
                        <Button asChild variant="outline" size="icon">
                          <a href={project.github} target="_blank" rel="noopener noreferrer" aria-label="View source code">
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                            </svg>
                          </a>
                        </Button>
                      )}
                    </>
                  ) : project.id === 'ithi-vscode' || project.id === 'zodia' ? (
                    <Button asChild variant="outline" className="w-full group/btn">
                      <Link to={`/case-study/${project.id}`}>
                        View Case Study
                        <ExternalLink className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  ) : (
                    <Button variant="ghost" className="w-full" disabled>
                      Details Coming Soon
                    </Button>
                  )}
                </CardFooter>
              </Card>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
};

export default Projects;
